import * as XLSX from "xlsx";
import { msg, t } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { BulkCreateMaterialItem } from "@/types/material";

/**
 * Bulk ingredient import from a supplier's Excel/CSV sheet (T-1201).
 *
 * The recipes importer round-trips our own export, so it can key on the exact
 * column labels we wrote. A supplier's item list has whatever headers the
 * supplier's system produces — so this flow adds a mapping step (user says
 * which column is which, seeded by a guess) and a validation step that flags
 * bad rows instead of silently dropping them. A dropped row looks like a
 * successful import that lost data; a flagged row is a decision the user made.
 *
 * Everything here is pure so it can be tested against a synthetic fixture —
 * the real Noble Bird export this was written for has not arrived yet.
 */

// ── Parsing ───────────────────────────────────────────────────────────────────

export interface ParsedSheet {
  sheetName: string;
  headers: string[];
  /** One record per data row, keyed by header. Blank rows are dropped. */
  rows: Record<string, unknown>[];
}

export function parseWorkbook(wb: XLSX.WorkBook): ParsedSheet {
  const sheetName = wb.SheetNames[0];
  const sheet = sheetName ? wb.Sheets[sheetName] : undefined;
  if (!sheetName || !sheet) {
    throw new Error("The file has no sheets");
  }

  // header: 1 gives raw arrays; the first non-empty array is the header row.
  const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: "" });
  const headerRow = grid.find((row) => row.some((cell) => String(cell).trim() !== ""));
  if (!headerRow) {
    throw new Error("The first sheet is empty");
  }
  const headers = headerRow.map((cell) => String(cell).trim()).filter(Boolean);

  const rows = XLSX.utils
    .sheet_to_json<Record<string, unknown>>(sheet, { defval: "" })
    .filter((row) => Object.values(row).some((cell) => String(cell).trim() !== ""));

  return { sheetName, headers, rows };
}

export async function parseSpreadsheetFile(file: File): Promise<ParsedSheet> {
  const buffer = await file.arrayBuffer();
  return parseWorkbook(XLSX.read(buffer, { type: "array" }));
}

// ── Target fields ─────────────────────────────────────────────────────────────

export type MaterialImportFieldKey =
  | "name"
  | "type"
  | "unitOfMeasure"
  | "packQuantity"
  | "packUom"
  | "recipeUom"
  | "unitCost"
  | "unitCostUnit"
  | "supplierName"
  | "sku"
  | "allergens"
  | "description";

export interface MaterialImportField {
  key: MaterialImportFieldKey;
  /** Shown in the mapping UI; the dialog translates it. */
  label: MessageDescriptor;
  required?: boolean;
  /**
   * Header shapes this field commonly arrives under. Ordered specific-first
   * within the field list below: `guessColumnMapping` walks fields in order and
   * each header can only be claimed once, so "Pack Size" must be considered
   * before the looser cost/unit patterns get a chance to swallow it.
   */
  synonyms: RegExp;
}

export const MATERIAL_IMPORT_FIELDS: MaterialImportField[] = [
  {
    key: "name",
    label: msg`Name`,
    required: true,
    synonyms: /^(item\s*)?(name|description of item|product|ingredient|material)s?$/i,
  },
  { key: "sku", label: msg`SKU`, synonyms: /^(sku|item\s*(no|number|#|code)|product\s*code)$/i },
  {
    key: "packQuantity",
    label: msg`Pack size`,
    synonyms: /pack\s*(size|qty|quantity|count)|case\s*(size|count|qty)|units?\s*per\s*(case|pack)/i,
  },
  { key: "packUom", label: msg`Pack unit`, synonyms: /pack\s*(uom|unit)|inner\s*unit/i },
  {
    key: "recipeUom",
    label: msg`Recipe unit`,
    synonyms: /recipe\s*(uom|unit)|usage\s*unit|cooking\s*unit/i,
  },
  {
    key: "unitOfMeasure",
    label: msg`Purchase unit`,
    required: true,
    synonyms: /purchase\s*(uom|unit)|order\s*(uom|unit)|^(uom|unit|unit\s*of\s*measure|sold\s*by)$/i,
  },
  {
    key: "unitCost",
    label: msg`Unit cost`,
    synonyms: /(unit|case|purchase)?\s*(cost|price)( per .+)?$/i,
  },
  { key: "unitCostUnit", label: msg`Cost unit`, synonyms: /cost\s*(uom|unit)|price\s*(uom|unit|per)/i },
  {
    key: "supplierName",
    label: msg`Supplier`,
    synonyms: /supplier|vendor|distributor|purveyor/i,
  },
  { key: "allergens", label: msg`Allergens`, synonyms: /allergen/i },
  { key: "type", label: msg`Category`, synonyms: /category|^type$|classification|group/i },
  { key: "description", label: msg`Description`, synonyms: /description|notes?$/i },
];

/** header claimed for each field — the thing the mapping step edits. */
export type ColumnMapping = Partial<Record<MaterialImportFieldKey, string>>;

export function guessColumnMapping(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  const claimed = new Set<string>();
  for (const field of MATERIAL_IMPORT_FIELDS) {
    const header = headers.find((h) => !claimed.has(h) && field.synonyms.test(h.trim()));
    if (header) {
      mapping[field.key] = header;
      claimed.add(header);
    }
  }
  return mapping;
}

// ── Validation ────────────────────────────────────────────────────────────────

export interface ValidationContext {
  /** Allergen codes the API will accept, upper-cased. */
  allergenCodes: Set<string>;
}

export interface ValidatedImportRow {
  /** 0-based position among the data rows — the API's `failed[].index` space. */
  index: number;
  /** 1-based spreadsheet row, counting the header — what the user sees. */
  rowNumber: number;
  name: string;
  /** The API payload for the row, or null when it has errors. */
  item: BulkCreateMaterialItem | null;
  errors: string[];
}

const MATERIAL_TYPES: Record<string, "RAW" | "COMPOUND" | "READY_TO_USE"> = {
  RAW: "RAW",
  COMPOUND: "COMPOUND",
  READY_TO_USE: "READY_TO_USE",
  "READY TO USE": "READY_TO_USE",
  RTU: "READY_TO_USE",
  PREPARED: "READY_TO_USE",
};

/**
 * A supplier sheet writes numbers the way a person does — "$1,234.50",
 * "1.234,50" would be nice too but is ambiguous, so only the anglophone shape
 * is unwrapped. Returns null for anything that is not a non-negative number.
 */
export function parseImportNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0 ? value : null;
  }
  const cleaned = String(value)
    .trim()
    .replace(/^[$€£]\s*/, "")
    .replace(/,(?=\d{3}(\D|$))/g, "");
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function cell(row: Record<string, unknown>, header: string | undefined): string {
  if (!header) return "";
  return String(row[header] ?? "").trim();
}

export function validateImportRows(
  rows: Record<string, unknown>[],
  mapping: ColumnMapping,
  ctx: ValidationContext,
): ValidatedImportRow[] {
  const seenNames = new Map<string, number>(); // lower-cased name → rowNumber
  return rows.map((row, index) => {
    const rowNumber = index + 2; // 1-based plus the header row
    const errors: string[] = [];

    const name = cell(row, mapping.name);
    if (!name) errors.push(t`Name is missing`);

    const unitOfMeasure = cell(row, mapping.unitOfMeasure);
    if (!unitOfMeasure) errors.push(t`Purchase unit is missing`);

    if (name) {
      const key = name.toLowerCase();
      const firstRow = seenNames.get(key);
      if (firstRow !== undefined) {
        errors.push(t`Duplicate of row ${firstRow}`);
      } else {
        seenNames.set(key, rowNumber);
      }
    }

    const item: BulkCreateMaterialItem = { name, unitOfMeasure };

    const rawType = cell(row, mapping.type);
    if (rawType) {
      const type = MATERIAL_TYPES[rawType.toUpperCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim()]
        ?? MATERIAL_TYPES[rawType.toUpperCase()];
      if (type) item.type = type;
      else errors.push(t`Unknown category "${rawType}" — use Raw, Compound, or Ready to Use`);
    }

    const rawPackQuantity = cell(row, mapping.packQuantity);
    if (rawPackQuantity) {
      const packQuantity = parseImportNumber(rawPackQuantity);
      if (packQuantity === null) errors.push(t`Pack size "${rawPackQuantity}" is not a number`);
      else item.packQuantity = packQuantity;
    }

    const packUom = cell(row, mapping.packUom);
    if (packUom) item.packUom = packUom;

    // Mirror the API's pair invariant here so the row is flagged in the
    // preview instead of coming back as a server-side failure at commit.
    if ((item.packQuantity !== undefined) !== (item.packUom !== undefined)) {
      errors.push(t`A pack size needs both a count and a unit`);
    }

    const recipeUom = cell(row, mapping.recipeUom);
    if (recipeUom) item.recipeUom = recipeUom;

    const rawUnitCost = cell(row, mapping.unitCost);
    if (rawUnitCost) {
      const unitCost = parseImportNumber(rawUnitCost);
      if (unitCost === null) errors.push(t`Unit cost "${rawUnitCost}" is not a number`);
      else item.unitCost = unitCost;
    }

    const unitCostUnit = cell(row, mapping.unitCostUnit);
    if (unitCostUnit) item.unitCostUnit = unitCostUnit;

    const supplierName = cell(row, mapping.supplierName);
    if (supplierName) item.supplierName = supplierName;

    const sku = cell(row, mapping.sku);
    if (sku) item.sku = sku;

    const description = cell(row, mapping.description);
    if (description) item.description = description;

    const rawAllergens = cell(row, mapping.allergens);
    if (rawAllergens) {
      const codes = rawAllergens
        .split(/[,;/]/)
        .map((code) => code.trim().toUpperCase().replace(/\s+/g, "_"))
        .filter(Boolean);
      const unknown = codes.filter((code) => !ctx.allergenCodes.has(code));
      if (unknown.length > 0) {
        // The API silently drops codes it does not know, which would read as
        // "imported fine" while the allergen quietly went missing — for food
        // safety data, the row has to fail loudly instead.
        const unknownList = unknown.join(", ");
        errors.push(
          unknown.length > 1
            ? t`Unknown allergens: ${unknownList}`
            : t`Unknown allergen: ${unknownList}`,
        );
      } else if (codes.length > 0) {
        item.allergenCodes = codes;
      }
    }

    return {
      index,
      rowNumber,
      name: name || t`Row ${rowNumber}`,
      item: errors.length === 0 ? item : null,
      errors,
    };
  });
}

/** The API payloads of the rows that passed, in row order. */
export function toBulkItems(rows: ValidatedImportRow[]): BulkCreateMaterialItem[] {
  return rows.flatMap((row) => (row.item ? [row.item] : []));
}
